import React, { useEffect, useContext, useState } from 'react'
import { Separator } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import { useLocation } from '@fs/zion-router'
import { css } from '@emotion/core'
import { boolAttr } from '@fs/zion-frontend-friends'
import { useTranslation } from 'react-i18next'
import { MenuNewWindow } from '@fs/zion-icon'
import Head, { Title } from '@fs/zion-head'
import ContentCardRow from '../ContentCardRow'
import TopDetailBar from '../TopDetailBar'
import SocialRow from '../social/SocialRow'
import SocialButton from '../social/SocialButton'
import { PlaylistContext } from '../PlaylistContext'
import RichTextDisplayer from '../RichTextDisplayer'
import { markItemsInPlaylist } from '../../global/playlistHelper'
import useIsMobile from '../../hooks/useIsMobile'
import getSocialsFromBrightspot from '../../helpers/BrightSpotSocialHelper'
import { useCurrentSpeaker } from '../SpeakerProvider'
import PillButton from '../PillButton'
import SpotlightGradient from '../SpotlightGradient'
import DetailsPageContainer from '../DetailsPageContainer'
import LargeLoadingSpinner from '../LargeLoadingSpinner'
import ContentContainer from '../ContentContainer'

const biographyCss = css`
  display: flex;
  justify-content: center;
  max-width: 800px;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  font-size: 18px;
  line-height: 28px;
`

const contentCss = css`
  display: grid;
  grid-column-gap: 48px;
  grid-row-gap: 24px;
  grid-template-areas: 'img info' 'img socials';
  justify-content: start;
  position: relative;
  z-index: 1;

  [is-mobile] & {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-items: center;
    text-align: center;
    grid-template-areas: 'img img' 'info info';
  }
`

const speakerImgCss = css`
  --size: 33vw;
  --max-size: 350px;
  border-radius: 50%;
  grid-area: img;
  height: var(--size);
  max-height: var(--max-size);
  max-width: var(--max-size);
  object-fit: cover;
  width: var(--size);
  background: #fff9;

  [is-mobile] & {
    --size: 50vw;
  }
`

const socialsCss = css`
  margin-top: 16px;
  grid-area: socials;
`

const speakerInfoCss = css`
  align-self: end;
  display: grid;
  grid-area: info;

  [is-mobile] & {
    align-self: center;
  }
`

const headingCss = css`
  font-size: 56px;
  line-height: 64px;
  font-weight: 700;
  &[is-mobile] {
    font-size: 24px;
    line-height: 28px;
  }
`

const subHeadingCss = css`
  font-size: 32px;
  line-height: 44px;
  margin: 4px 0;
  &[is-mobile] {
    margin-bottom: 16px;
    font-size: 18px;
    line-height: 24px;
  }
`

const SpeakerDetail = () => {
  const [t] = useTranslation()
  const location = useLocation()
  const { data, loading } = useCurrentSpeaker()
  const { playlist } = useContext(PlaylistContext)
  const [sessionsData, setSessionsData] = useState()
  const isMobile = useIsMobile()
  usePageView({ page_detail: `${location.pathname}` })
  const socialPlatforms = getSocialsFromBrightspot(
    ['linkedIn', 'instagram', 'facebook', 'youtube', 'twitter'],
    data?.RootsTechPageEntry
  )

  useEffect(() => {
    if (data?.RootsTechPageEntry?.submissions) {
      const sessionsWithSpeaker = data?.RootsTechPageEntry?.submissions
      const playlistMarkedList = markItemsInPlaylist({ playlist, items: sessionsWithSpeaker })
      setSessionsData(playlistMarkedList)
    }
  }, [playlist, data])

  const socialButtons =
    socialPlatforms?.length > 0 ? (
      <>
        <SocialRow isMobile={isMobile}>
          {socialPlatforms?.map((platform) => (
            <SocialButton
              key={platform.platform}
              isMobile={isMobile}
              platform={platform.platform.toLowerCase()}
              to={platform.url}
              external
              emphasis="inline"
            />
          ))}
        </SocialRow>
        {data?.RootsTechPageEntry?.businessWebsite && (
          <PillButton
            style={{ marginTop: 24 }}
            data-testid="menu-new-window"
            fitContent
            noBorder
            to={data?.RootsTechPageEntry.businessWebsite}
          >
            <MenuNewWindow />
            <div style={{ marginLeft: 10 }}>{data?.RootsTechPageEntry.businessWebsite}</div>
          </PillButton>
        )}
      </>
    ) : null

  return (
    <>
      <Title breadcrumbs={[t('site-title', 'RootsTech 2021')]}>{data?.RootsTechPageEntry?.name}</Title>

      <Head>
        <meta property="og:url" content={window.location.href} />
        <meta
          property="og:title"
          content={`${data?.RootsTechPageEntry?.name} \u2022 ${data?.RootsTechPageEntry?.title} \u2022 ${t(
            'site-title',
            'RootsTech 2021'
          )} \u2022 FamilySearch `}
        />
        <meta property="og:image" content={data?.RootsTechPageEntry?.profilePhoto?.custom?.image?.src} />
      </Head>

      <SpotlightGradient flavor="apple" size="md" />
      <ContentContainer>
        {loading ? (
          <LargeLoadingSpinner />
        ) : (
          <DetailsPageContainer>
            <TopDetailBar backText={t('common-ui:back.action', 'Back')} backLink="/speakers" />
            {!isMobile && <Separator size="xs" />}

            <div css={contentCss}>
              <img
                css={speakerImgCss}
                no-socials={boolAttr(socialPlatforms?.length === 0)}
                src={data?.RootsTechPageEntry?.profilePhoto?.custom?.image?.src}
                alt=""
                aria-hidden
              />
              <div css={speakerInfoCss}>
                <h1 css={headingCss} is-mobile={boolAttr(isMobile)}>
                  {data?.RootsTechPageEntry?.name}
                </h1>
                <p css={subHeadingCss} is-mobile={boolAttr(isMobile)}>
                  {data?.RootsTechPageEntry?.title}
                </p>
                {isMobile && socialButtons}
              </div>
              {!isMobile && <div css={socialsCss}>{socialButtons}</div>}
            </div>
            <Separator size={isMobile ? 'sm' : 'xl'} />
            <div css={biographyCss}>
              <RichTextDisplayer richText={data?.RootsTechPageEntry?.biography} />
            </div>
            <Separator size={isMobile ? 'md' : 'xl'} />
            <ContentCardRow
              headingText={t('speakers.sessions-with-this-speaker.label', 'Sessions with this speaker')}
              useHeadingDivider
              items={sessionsData}
              isPlaylistable
            />
          </DetailsPageContainer>
        )}
      </ContentContainer>
    </>
  )
}

export default SpeakerDetail
